/**
 * WebSocket Service for Real-time Notifications
 * 
 * Manages WebSocket connection to backend for real-time escalation notifications,
 * SLA alerts, and other dashboard updates.
 */

class WebSocketService {
  constructor() {
    this.ws = null;
    this.userId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 3000; // 3 seconds
    this.listeners = new Map();
    this.isConnecting = false;
    this.permanentlyFailed = false; // Flag to stop reconnection attempts
    this.retryTimeout = null; // Timeout for retry after permanent failure
  }

  /**
   * Connect to WebSocket server
   * @param {number} userId - User ID for the connection
   * @param {function} onMessage - Callback for received messages
   * @param {function} onError - Callback for errors
   */
  connect(userId, onMessage, onError) {
    // Always reset permanentlyFailed flag on new connection attempt
    // This allows reconnection after page refresh or manual retry
    // The flag is only used to prevent infinite reconnection loops during the same session
    if (this.userId !== userId || !this.ws || this.ws.readyState === WebSocket.CLOSED) {
      // Different user or connection is closed - reset flags to allow fresh connection
      this.permanentlyFailed = false;
      this.reconnectAttempts = 0;
      if (this.retryTimeout) {
        clearTimeout(this.retryTimeout);
        this.retryTimeout = null;
      }
    }

    // If already connected to the same user, just update listeners
    if (this.ws && this.ws.readyState === WebSocket.OPEN && this.userId === userId) {
      // Already connected to same user - just update handlers
      if (onMessage) {
        this.on('escalation_notification', onMessage);
      }
      return;
    }

    // If connecting to different user or connection is closed/closing, disconnect first
    if (this.ws && (this.userId !== userId || 
        (this.ws.readyState !== WebSocket.OPEN && this.ws.readyState !== WebSocket.CONNECTING))) {
      this.disconnect();
    }

    // If already connecting to the same user, wait for it
    if (this.isConnecting && this.userId === userId) {
      // Connection in progress, just update listeners
      if (onMessage) {
        this.on('escalation_notification', onMessage);
      }
      return;
    }

    this.userId = userId;
    this.isConnecting = true;
    // Don't reset reconnectAttempts here - let it accumulate for reconnection attempts

    // Get authentication token
    const token = localStorage.getItem('token');
    if (!token) {
      if (process.env.NODE_ENV === 'development') {
        console.error('No authentication token found for WebSocket connection');
      }
      if (onError) {
        onError(new Error('Authentication required'));
      }
      return;
    }

    // Determine WebSocket URL
    // Priority: REACT_APP_WS_URL > REACT_APP_API_URL > window.location (for proxy)
    let wsUrl;
    
    // Check if explicit WebSocket URL is provided
    if (process.env.REACT_APP_WS_URL) {
      const wsBaseUrl = process.env.REACT_APP_WS_URL.replace(/\/$/, ''); // Remove trailing slash
      // Ensure protocol is ws:// or wss://
      if (wsBaseUrl.startsWith('ws://') || wsBaseUrl.startsWith('wss://')) {
        wsUrl = `${wsBaseUrl}/api/v1/ws/${userId}?token=${encodeURIComponent(token)}`;
      } else {
        // Auto-detect protocol based on current page
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        wsUrl = `${protocol}//${wsBaseUrl.replace(/^https?:\/\//, '')}/api/v1/ws/${userId}?token=${encodeURIComponent(token)}`;
      }
    } else if (process.env.REACT_APP_API_URL) {
      // Use API URL and convert to WebSocket protocol
      const apiUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
      const protocol = apiUrl.startsWith('https://') ? 'wss:' : 'ws:';
      const host = apiUrl.replace(/^https?:\/\//, '');
      wsUrl = `${protocol}//${host}/api/v1/ws/${userId}?token=${encodeURIComponent(token)}`;
    } else if (process.env.NODE_ENV === 'development') {
      // Development: Use same protocol/host as current page (will be proxied by setupProxy.js)
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/v1/ws/${userId}?token=${encodeURIComponent(token)}`;
    } else {
      // Production fallback: Use current host with appropriate protocol
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/v1/ws/${userId}?token=${encodeURIComponent(token)}`;
    }
    
    // Log connection attempt (mask token for security in logs)
    const maskedUrl = wsUrl.replace(/token=[^&]+/, 'token=***');
    if (process.env.NODE_ENV === 'development') {
      console.log(`WebSocket: Connecting to ${maskedUrl}`);
    }

    try {
      this.ws = new WebSocket(wsUrl);

      this.ws.onopen = () => {
        if (process.env.NODE_ENV === 'development') {
          console.log('WebSocket: Connected successfully');
        }
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.permanentlyFailed = false; // Reset on successful connection
        
        // Register message handler
        if (onMessage) {
          this.on('escalation_notification', onMessage);
        }
        
        // Verify connection by checking if backend responds to ping
        this.verifyConnection(onError);
        
        // Send ping to keep connection alive
        this.startHeartbeat();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          // Handle pong response (silent - no logging needed)
          if (data.type === 'pong') {
            return;
          }
          
          // Call specific listeners for message type (more efficient)
          if (data.type && this.listeners.has(data.type)) {
            const callbacks = this.listeners.get(data.type);
            if (process.env.NODE_ENV === 'development' && data.type !== 'pong') {
              console.log(`WebSocket: ${data.type} message received`);
            }
            callbacks.forEach(callback => {
              try {
                callback(data);
              } catch (error) {
                if (process.env.NODE_ENV === 'development') {
                  console.error('WebSocket: Error in listener:', error);
                }
              }
            });
          } else if (process.env.NODE_ENV === 'development' && data.type) {
            console.warn(`WebSocket: No listeners for type "${data.type}"`);
          }
        } catch (error) {
          if (process.env.NODE_ENV === 'development') {
            console.error('WebSocket: Parse error:', error);
          }
        }
      };

      this.ws.onerror = (error) => {
        const errorDetails = {
          readyState: this.ws?.readyState,
          url: maskedUrl,
          protocol: this.ws?.protocol || '',
          extensions: this.ws?.extensions || ''
        };
        
        if (process.env.NODE_ENV === 'development') {
          console.error('WebSocket error:', error);
          console.error('WebSocket error details:', errorDetails);
        }
        
        this.isConnecting = false;
        
        // Provide more detailed error information
        const errorMessage = this.ws?.readyState === WebSocket.CLOSED 
          ? 'WebSocket connection failed. The server may be unreachable or the WebSocket endpoint may not be available.'
          : 'WebSocket error occurred';
        
        if (onError) {
          onError(new Error(errorMessage));
        }
      };

      this.ws.onclose = (event) => {
        if (process.env.NODE_ENV === 'development') {
          console.log(`WebSocket closed`, {
            code: event.code,
            reason: event.reason || '',
            wasClean: event.wasClean,
            url: maskedUrl
          });
        }
        this.isConnecting = false;
        this.stopHeartbeat();
        
        // Don't reconnect on authentication errors (1008) or policy violations
        if (event.code === 1008) {
          // Authentication failed or policy violation - don't retry
          if (onError) {
            onError(new Error('WebSocket authentication failed'));
          }
          return;
        }
        
        // Don't reconnect on normal closure (1000) or going away (1001 - page refresh/navigation)
        // These are intentional closures, component will reconnect on mount if needed
        if (event.code === 1000 || event.code === 1001) {
          // Reset connection state to allow fresh connection on next mount
          this.ws = null;
          this.isConnecting = false;
          return;
        }
        
        // Code 1006 (abnormal closure) often indicates connection refused or network issues
        if (event.code === 1006 && process.env.NODE_ENV === 'development') {
          console.warn('WebSocket connection refused. Check if the WebSocket server is running and accessible.');
        }
        
        // Attempt to reconnect if not a normal closure and haven't exceeded max attempts
        if (this.reconnectAttempts < this.maxReconnectAttempts) {
          this.reconnectAttempts++;
          if (process.env.NODE_ENV === 'development') {
            console.log(`Reconnecting in ${this.reconnectDelay / 1000} seconds... (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
          }
          setTimeout(() => {
            // Only reconnect if we haven't exceeded max attempts and still need to connect
            if (this.reconnectAttempts <= this.maxReconnectAttempts && this.userId === userId) {
              this.connect(userId, onMessage, onError);
            }
          }, this.reconnectDelay);
        } else {
          // Max attempts reached - mark as permanently failed and stop trying
          this.permanentlyFailed = true;
          if (process.env.NODE_ENV === 'development') {
            console.warn('Max WebSocket reconnection attempts reached. Falling back to polling.');
          }
          if (onError) {
            onError(new Error('WebSocket connection failed after max attempts'));
          }
        }
      };
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('WebSocket: Failed to create connection:', error);
      }
      this.isConnecting = false;
      if (onError) {
        onError(error);
      }
    }
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.stopHeartbeat();
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    this.userId = null;
    this.listeners.clear();
    // Don't reset permanentlyFailed on manual disconnect - let it reset on successful reconnect
  }

  /**
   * Register a listener for a specific message type
   * @param {string} messageType - Type of message to listen for
   * @param {function} callback - Callback function
   */
  on(messageType, callback) {
    if (!this.listeners.has(messageType)) {
      this.listeners.set(messageType, []);
    }
    this.listeners.get(messageType).push(callback);
  }

  /**
   * Remove a listener
   * @param {string} messageType - Type of message
   * @param {function} callback - Callback function to remove
   */
  off(messageType, callback) {
    if (this.listeners.has(messageType)) {
      const callbacks = this.listeners.get(messageType);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  }

  /**
   * Send a message through WebSocket
   * @param {object} message - Message to send
   */
  send(message) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    }
    // Silently fail if not connected
  }

  /**
   * Start heartbeat to keep connection alive
   */
  startHeartbeat() {
    this.heartbeatInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send('ping');
      }
    }, 30000); // Send ping every 30 seconds
  }

  /**
   * Stop heartbeat
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
  }

  /**
   * Check if WebSocket is connected
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Verify connection by sending a ping and waiting for pong response
   * If no pong is received within timeout, treat as failed connection
   */
  verifyConnection(onError) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return;
    }

    let pongReceived = false;
    const verificationTimeout = 5000; // 5 seconds timeout
    const originalOnMessage = this.ws.onmessage;

    // Set up temporary message handler to detect pong
    this.ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'pong') {
          pongReceived = true;
          // Restore original message handler
          this.ws.onmessage = originalOnMessage;
          // Process the pong message with original handler
          if (originalOnMessage) {
            originalOnMessage(event);
          }
          return;
        }
      } catch (e) {
        // Not JSON, ignore
      }
      
      // Call original handler for other messages
      if (originalOnMessage) {
        originalOnMessage(event);
      }
    };

    // Send verification ping
    this.ws.send('ping');

    // Set timeout to detect if connection is fake
    setTimeout(() => {
      if (!pongReceived && process.env.NODE_ENV === 'development') {
        console.warn('WebSocket: Verification failed - no pong received');
        // Restore original message handler
        this.ws.onmessage = originalOnMessage;
        // Don't close the connection, but log the warning
        // The connection might still work for receiving messages
      }
    }, verificationTimeout);
  }
}

// Export singleton instance
export const websocketService = new WebSocketService();
